/**
 * EVENTSFLO-FRONTEND-UPDATE-AVAILABLE-REFRESH-TOAST-V1
 *
 * Non-blocking "Update available — refresh" notification.
 *
 * Subscribes to the Service Worker status via `subscribeSwStatus()` and
 * shows ONE persistent sonner toast when a waiting SW is detected. The
 * toast offers:
 *   - "Refresh now"  → calls `activateWaitingAndReload()` which posts
 *                       skipWaiting + lets `controllerchange` reload
 *                       the page exactly once.
 *   - "Later"        → dismisses the toast AND stores a session-scoped
 *                       suppression flag keyed to the new build, so we
 *                       don't nag the user again in the same tab.
 *
 * Designed to be safe to mount at the root of the React tree. It renders
 * nothing visible itself — sonner owns the toast UI.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { subscribeSwStatus, activateWaitingAndReload } from "../../lib/registerSw";

const TOAST_ID = "sw-update-available";
// We key suppression by the live build that's offering the update.
// `status.version` is the BUILD_VERSION constant baked into the running
// bundle — i.e. the OLD build. The user is being asked to move FROM that
// to whatever the SW just downloaded. Suppressing per-current-version
// means the toast will reappear the next time a brand-new build arrives.
const SUPPRESS_KEY = (currentVersion) =>
  `icrew:sw-update-suppressed:${currentVersion}`;

function isSuppressed(version) {
  try {
    return sessionStorage.getItem(SUPPRESS_KEY(version)) === "1";
  } catch {
    return false;
  }
}

function markSuppressed(version) {
  try {
    sessionStorage.setItem(SUPPRESS_KEY(version), "1");
  } catch {
    /* private mode / quota — best-effort */
  }
}

export default function SwUpdateToast() {
  // Track whether we've already shown the toast this lifetime so we don't
  // double-stack it when listeners fire multiple times.
  const shownRef = useRef(false);

  useEffect(() => {
    const unsub = subscribeSwStatus((status) => {
      if (!status?.supported) return;
      if (!status.updateAvailable) return;
      if (shownRef.current) return;
      if (isSuppressed(status.version)) return;

      shownRef.current = true;
      toast(
        "Update available",
        {
          id: TOAST_ID,
          description:
            "A new version of EventsFlo is available. Refresh to update.",
          duration: Infinity,
          dismissible: true,
          action: {
            label: "Refresh now",
            onClick: () => {
              try {
                activateWaitingAndReload();
              } catch (err) {
                console.warn("[SwUpdateToast] activate failed:", err);
                // Belt-and-braces fallback.
                window.location.reload();
              }
            },
          },
          cancel: {
            label: "Later",
            onClick: () => {
              markSuppressed(status.version);
              toast.dismiss(TOAST_ID);
              // We deliberately KEEP shownRef.current === true here so a
              // dismissed toast does not immediately re-appear if the
              // update listener fires again for the same pending SW.
              // A genuinely new update (different version, different SW
              // lifecycle event) is rare in one tab session, and the
              // shownRef guard prevents accidental spam in the meantime.
            },
          },
          // data-testid for headless verification.
          // sonner passes className through to the toast root.
          className: "sw-update-toast",
        }
      );
    });
    return () => {
      unsub?.();
    };
  }, []);

  return null;
}
